import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import AIAgent from "./pages/AIAgent";
import Goals from "./pages/Goals";
import Settings from "./pages/Settings";
import Subscription from "./pages/Subscription";
import Login from "./pages/Login";
import CROAnalyzer from "./pages/CROAnalyzer";
import Landing from "./pages/Landing";
import OnboardingFlow from "./pages/OnboardingFlow";
import LoadingSpinner from "./components/LoadingSpinner";
import PlanProtectedRoute from "./components/PlanProtectedRoute";
import Connectors from "./pages/Connectors";
import ConnectorSetup from "./pages/connector/ConnectorSetup";
import OAuthCallback from "./pages/connector/OAuthCallback";
import ConnectionManager from "./pages/connector/ConnectionManager";
import Connections from "./pages/Connections";

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return user ? children : <Navigate to="/login" replace />;
};

/**
 * Main App Layout Component
 * Includes sidebar navigation for authenticated users
 * Responsive: Sidebar is a drawer on mobile, fixed on desktop
 */
const AppLayout = ({ children }) => {
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onMenuToggle={toggleSidebar} />
      <div className="flex">
        {user && <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />}
        <main className={`flex-1 ${user ? "lg:ml-64" : ""} pt-16`}>
          <div className="p-4 md:p-6">{children}</div>
        </main>
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            style: {
              background: "#10b981",
            },
          },
          error: {
            style: {
              background: "#ef4444",
            },
          },
        }}
      />
    </div>
  );
};

/**
 * Main App Component
 * Handles routing and authentication state
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Login />} />
            <Route path="/analyze" element={<Landing />} />
            <Route path="/start-analysis" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingFlow />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <PlanProtectedRoute feature="dashboard">
                    <Dashboard />
                  </PlanProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/ai-agent"
              element={
                <ProtectedRoute>
                  <PlanProtectedRoute feature="ai_agent">
                    <AIAgent />
                  </PlanProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <PlanProtectedRoute feature="goals">
                    <Goals />
                  </PlanProtectedRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/connectors"
              element={
                <ProtectedRoute>
                  <Connectors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connectors/:connectorId/setup"
              element={
                <ProtectedRoute>
                  <ConnectorSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connectors/oauth/callback/:instanceId"
              element={
                <ProtectedRoute>
                  <OAuthCallback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasources/:instanceId"
              element={
                <ProtectedRoute>
                  <ConnectionManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connections"
              element={
                <ProtectedRoute>
                  <Connections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cro-analyzer"
              element={
                <ProtectedRoute>
                  <CROAnalyzer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cro-analyzer/:analysisId"
              element={
                <ProtectedRoute>
                  <CROAnalyzer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  );
}

export default App;
